import { loginAdmin } from '@gozoki/api'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import loginLogo from '~/assets/logos/logoSoft.svg'
import { PasswordResetStep } from '~/components/routes/login/password-reset-step'
import {
  codeRequestOpensEntry,
  UNKNOWN_ERROR_MESSAGE,
  zResetEmail,
} from '~/components/routes/login/password-reset'
import { Alert, AlertDescription } from '~/components/ui/alert'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { useAppForm } from '~/hooks/form-hooks'
import { api } from '~/lib/tuyau'
import { getApiErrorMessage, getTuyauErrorStatus } from '~/lib/utils'
import { signIn } from '~/utils/session-navigation'
import { getErrorMessageWithSentry } from '~/utils/sentry'

export const Route = createFileRoute('/login')({
  component: LoginPage,
})

const zLogin = z.object({
  email: zResetEmail,
  password: z.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères'),
})

function LoginPage() {
  // Adresse de l'étape de saisie du code de réinitialisation, ouverte après une demande de code ou
  // par « J'ai déjà un code ».
  const [resetEmail, setResetEmail] = useState<string | null>(null)
  const [loginError, setLoginError] = useState<string | null>(null)

  const requestCode = useMutation(api.auth.askResetPasswordAdmin.mutationOptions())

  /** Ouvre la session avec le jeton d'une connexion, par mot de passe ou après une réinitialisation. */
  const openSession = async (token: string) => {
    try {
      await signIn(token)
    } catch (error) {
      setResetEmail(null)
      setLoginError(getErrorMessageWithSentry(error))
    }
  }

  const form = useAppForm({
    defaultValues: { email: '', password: '' },
    validators: { onSubmit: zLogin },
    onSubmit: async ({ value }) => {
      setLoginError(null)
      try {
        const { token } = await loginAdmin({ email: value.email.trim(), password: value.password })
        await openSession(token)
      } catch (error) {
        setLoginError(getErrorMessageWithSentry(error))
      }
    },
  })

  const askResetCode = () => {
    const email = form.getFieldValue('email').trim()
    if (!zResetEmail.safeParse(email).success) {
      toast.error(`L'email "${email}" n'est pas valide. Entrez votre email`)
      return
    }

    requestCode.mutate(
      { body: { email } },
      {
        onSuccess: (response) => {
          // Le type inclut `undefined` (branche du limiteur, qui répond sans corps typé).
          if (response) toast.success(response.message)
          setResetEmail(email)
        },
        onError: (error) => {
          toast.error(getApiErrorMessage(error, UNKNOWN_ERROR_MESSAGE))
          // Trop de demandes : le dernier code envoyé reste valable, la saisie s'ouvre quand même.
          if (codeRequestOpensEntry(getTuyauErrorStatus(error))) setResetEmail(email)
        },
      },
    )
  }

  if (resetEmail !== null) {
    return (
      <PasswordResetStep
        email={resetEmail}
        onBack={() => setResetEmail(null)}
        onPasswordReset={openSession}
      />
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="justify-items-center text-center">
          <img src={loginLogo} alt="Lucie" height="60" width="95" className="mb-4" />
          <CardTitle className="text-lg">Back Office</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(event) => {
              event.preventDefault()
              event.stopPropagation()
              void form.handleSubmit()
            }}
            className="flex flex-col gap-4"
            noValidate
          >
            <form.AppField name="email">
              {(field) => <field.TextField label="Email" type="email" autoComplete="username" />}
            </form.AppField>
            <form.AppField name="password">
              {(field) => (
                <field.TextField
                  label="Mot de passe"
                  type="password"
                  autoComplete="current-password"
                />
              )}
            </form.AppField>

            {loginError && (
              <Alert variant="destructive">
                <AlertDescription className="text-destructive">{loginError}</AlertDescription>
              </Alert>
            )}

            <form.AppForm>
              <form.SubmitButton className="w-full">Se connecter</form.SubmitButton>
            </form.AppForm>

            <div className="flex items-center justify-between gap-2">
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={askResetCode}
                disabled={requestCode.isPending}
              >
                Mot de passe oublié ?
              </Button>
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={() => setResetEmail(form.getFieldValue('email').trim())}
              >
                J'ai déjà un code
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
